import { useState } from 'react';

import type { TimeAttendanceMarkType } from '@/generated/graphql/types';

import {
  buildMockContext,
  computeMarkStatus,
  getMockState,
  MOCK_SCENARIO_PARAM,
  MOCK_TIMEZONE,
  MUTATION_ERROR_MOCKS,
  MUTATION_MOCK_PARAM,
  nowIso,
  todayDateString,
  updateMockMark,
} from '../../mocks';
import type {
  TMarkResult,
  TUseRecordAttendanceMarkReturn,
} from './use-record-attendance-mark.types';

// Simulates network latency so the UI shows loading state briefly
const MOCK_DELAY_MS = 800;

/** Returns a mock mutation when ?mock_scenario=<key> is in the URL.
 * Returns undefined when no mock param is present (real hook should be used).
 *
 * On success:
 *  1. Computes status for the mark type (ON_TIME, LATE, etc.)
 *  2. Updates the shared mock state with marked_at=now
 *  3. Triggers the query mock to re-render with updated marks
 *  4. Returns the mark + rebuilt context (same as real mutation response) */
export const useRecordAttendanceMarkMock = ():
  | TUseRecordAttendanceMarkReturn
  | undefined => {
  const params = new URLSearchParams(window.location.search);
  const scenarioKey = params.get(MOCK_SCENARIO_PARAM) ?? '';
  const mutationMockKey = params.get(MUTATION_MOCK_PARAM) ?? '';

  const [loading, setLoading] = useState(false);

  // No mock param → use real hook
  if (!scenarioKey) {
    return undefined;
  }

  const recordMark = async (
    markType: TimeAttendanceMarkType,
  ): Promise<TMarkResult> => {
    setLoading(true);
    await new Promise<void>(resolve => {
      setTimeout(resolve, MOCK_DELAY_MS);
    });
    setLoading(false);

    // Force a mutation error if &mutation_mock=<key> is in the URL
    if (mutationMockKey && MUTATION_ERROR_MOCKS[mutationMockKey]) {
      return MUTATION_ERROR_MOCKS[mutationMockKey];
    }

    const state = getMockState();

    if (!state) {
      return {
        success: false,
        errorCode: 'NO_MOCK_STATE',
        message: 'Mock state not initialized',
      };
    }

    // Check if this mark was already registered (prevent double-click)
    const existingMark = state.marks.find(m => m.mark_type === markType);

    if (existingMark?.marked_at) {
      return {
        success: false,
        errorCode: 'MARK_ALREADY_REGISTERED',
        message: `${markType} ya fue registrado`,
      };
    }

    // Compute status based on current time vs shift schedule
    const status = computeMarkStatus(markType, state.shiftContext);
    const markedAt = nowIso();

    // Update the shared state → triggers query mock re-render
    updateMockMark(markType, status, markedAt);

    // Rebuild context with the newly updated marks
    const updatedState = getMockState();

    if (!updatedState) {
      return {
        success: false,
        errorCode: 'STATE_ERROR',
        message: 'Failed to update mock state',
      };
    }

    return {
      success: true,
      mark: {
        id: `mock-${Date.now()}`,
        mark_type: markType,
        mark_date: todayDateString(),
        marked_at: markedAt,
        timezone: MOCK_TIMEZONE,
        status,
      },
      context: buildMockContext(updatedState),
    };
  };

  return { recordMark, loading };
};
